// File: src/pages/reveal/RevealCodeVerification.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import RevealPortalNavBar from "../../components/Reveal/RevealPortalNavBar";
import {
  getRevealSocket,
  startRevealAccessWatch,
  stopRevealAccessWatch,
  startLocationHeartbeat,
  stopLocationHeartbeat,
} from "../../utils/revealSocket";
import { enforceRevealState } from '../../utils/revealEnforcer';

import axios from "axios";

/* ---------------- DEBUG LOGGER ---------------- */
const DBG = true;
const TAG = "[Reveal/Verify]";
function L(...args) {
  if (DBG) console.log(TAG, ...args);
}

/* -------------------------------- helpers -------------------------------- */
function authHeaders() {
  const token =
    localStorage.getItem("accessToken") ||
    JSON.parse(localStorage.getItem("tribe_auth") || "{}")?.token ||
    "";
  return token ? { headers: { Authorization: `Bearer ${token}` } } : {};
}
function getMyId() {
  const auth = JSON.parse(localStorage.getItem("tribe_auth") || "{}");
  return (
    auth?.uid ||
    auth?.userId ||
    auth?.user?._id ||
    localStorage.getItem("userId") ||
    ""
  );
}
function toAge(dob) {
  if (!dob) return null;
  const d = new Date(dob);
  if (Number.isNaN(d)) return null;
  const n = new Date();
  let a = n.getFullYear() - d.getFullYear();
  const m = n.getMonth() - d.getMonth();
  if (m < 0 || (m === 0 && n.getDate() < d.getDate())) a--;
  return a >= 0 && a < 130 ? a : null;
}

/** Try to read the latest reveal `/match` snapshot we saved in sessionStorage */
function readPreload() {
  try {
    const raw = sessionStorage.getItem("reveal_preload_match");
    if (!raw) return null;
    const data = JSON.parse(raw);
    if (!data || data.state !== "match" || !Array.isArray(data.matches)) return null;
    const first = data.matches[0];
    return {
      eventId: data.eventId,
      matchId: first?.matchId || null,
      roundEndAt: first?.roundEndAt || data?.roundEndAt || null,
      partner: first?.partner || null,
      partnerId: first?.partnerId || null,
    };
  } catch {
    return null;
  }
}

/** Minimal partner render object (fallback if snapshot missing) */
function composePartnerFromMatch(match, myId) {
  if (!match) return null;
  const other =
    (match.participants || []).find((p) => String(p?._id) !== String(myId)) || null;
  if (!other) return null;
  return {
    _id: String(other._id || ""),
    firstName: other.firstName || "Partner",
    lastName: other.lastName || "",
    gender: other.gender || "",
    age: other.age ?? toAge(other.dateOfBirth),
    profilePicUrl: other.profilePicUrl || "/default-avatar.png",
  };
}

function readPreloadMatchesCount() {
  try {
    const raw = sessionStorage.getItem("reveal_preload_match");
    if (!raw) return null;
    const data = JSON.parse(raw);
    if (data?.state === "match" && Array.isArray(data.matches)) {
      return data.matches.length;
    }
    return null;
  } catch {
    return null;
  }
}
async function fetchMatchesCount(api, eventId) {
  try {
    const { data, status } = await axios.get(
      `${api}/reveal-mode/${eventId}/match?t=${Date.now()}`,
      authHeaders()
    );
    L("GET /reveal-mode/:id/match =>", status, data?.state, { cnt: data?.matches?.length });
    if (data?.state === "match" && Array.isArray(data.matches)) {
      return data.matches.length;
    }
  } catch (e) {
    L("fetchMatchesCount error", e?.response?.status, e?.message);
  }
  return null;
}
/** Resolve an end time to use for the 5-minute stage, preferring server time if present. */
function getStageEndsAtFallback(match, endTargetRef) {
  const fromMatch = match?.endTime || match?.expiresAt || null;
  const out = fromMatch
    ? new Date(fromMatch).getTime()
    : (endTargetRef?.current && Number.isFinite(endTargetRef.current))
      ? endTargetRef.current
      : Date.now() + 5 * 60 * 1000;
  return out;
}

/* -------------------------------- component -------------------------------- */
export default function RevealCodeVerification() {
  const { eventId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const api = process.env.REACT_APP_API_URL;
  
  const socket = useMemo(() => getRevealSocket(), []);
  const userId = getMyId();

  const [match, setMatch] = useState(null);
  const [matchId, setMatchId] = useState(location.state?.matchId || null);
  const [partnerSnap, setPartnerSnap] = useState(location.state?.partner || null);

  const [verificationSuccess, setVerificationSuccess] = useState(false);
  const [countdownValue, setCountdownValue] = useState(3);

  const [booting, setBooting] = useState(true);
  const [shouldShake, setShouldShake] = useState(false);

  const [codeDigits, setCodeDigits] = useState(["", "", ""]);
  const codeDigitsRefs = useRef([useRef(null), useRef(null), useRef(null)]);

  const [tparts, setTparts] = useState({ h: "--", m: "--", s: "--" });
  const hasHours = tparts.h !== "00" && tparts.h !== "--";

  const mountedRef = useRef(true);
  const tickIdRef = useRef(null);
  const lastShownRef = useRef(null);
  const endTargetRef = useRef(null);
  const refreshingRef = useRef(false);

  

  /* ---------------- access / presence ---------------- */
  useEffect(() => {
    L("mount", { eventId, locationState: location.state });
    startLocationHeartbeat();
    startRevealAccessWatch(eventId, {
      onDenied: (data) => {
        L("ACCESS DENIED watchdog → navigate portal", data);
        navigate(`/events/${eventId}`, { replace: true });
      },
      intervalMs: 15000,
    });
    return () => {
      stopRevealAccessWatch(eventId);
      stopLocationHeartbeat();
      L("unmount");
    };
  }, [eventId, navigate]); // eslint-disable-line


  useEffect(() => {
    if (!socket) return;
    function onStageUnlocked(_payload) {
      // While user is on Verify, do nothing.
      // Partner page will handle the unlock visually.
    }
    socket.on("reveal:stage-unlocked", onStageUnlocked);
    return () => socket.off("reveal:stage-unlocked", onStageUnlocked);
  }, [socket]);
  


  useEffect(() => {
    const s = getRevealSocket();
    const enter = () => { try { s.emit('revealPortal:enter', { eventId }); } catch {} };
    enter();
    s.on('connect', enter);
    return () => s.off('connect', enter);
  }, [eventId]);

  useEffect(() => {
    enforceRevealState({ api, eventId, pageKind: 'partner', navigate });
  }, [api, eventId, navigate]);

  // ── Pre-check: bounce away from Verify if server already says portal/inmatch/ended
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await axios.get(
          `${api}/reveal-mode/${eventId}/state?t=${Date.now()}`,
          { ...authHeaders(), validateStatus: (s) => [200,204,410].includes(s) }
        );
        if (!alive) return;
        if (res.status === 410) {
          navigate(`/reveal/${eventId}/portal`, { replace: true, state: { revealExpired: true } });
          return;
        }
        const route = String(res?.data?.route || '');
        if (route === 'portal' || route === 'inmatch') {
          navigate(`/reveal/${eventId}/${route}`, { replace: true });
          return;
        }
        // route 'partner' → stay on Verify (verify is part of partner flow)
      } catch {
        /* fail-open */
      }
    })();
    return () => { alive = false; };
  }, [api, eventId, navigate]);

  // ── Follow authoritative route pushes while on Verify
  useEffect(() => {
    const s = getRevealSocket();

    function onRoute(p) {
      const route = String(p?.route || '');
      if (!route) return;
      if (route === 'portal' || route === 'inmatch') {
        navigate(`/reveal/${eventId}/${route}`, {
          replace: true,
          state: { stage: p.stageIndex ?? null, matchId: p.matchId ?? null, fromSocket: true },
        });
      }
    }
    function onEpoch({ eventId: eid }) {
      if (String(eid) !== String(eventId)) return;
      navigate(`/reveal/${eventId}/portal`, { replace: true });
    }

    s.on('reveal:route', onRoute);
    s.on('reveal:epoch', onEpoch);
    return () => {
      s.off('reveal:route', onRoute);
      s.off('reveal:epoch', onEpoch);
    };
  }, [eventId, navigate]);

  // ── DOM route-intent relay (keeps Verify consistent with other pages)
  useEffect(() => {
    function onRouteIntent(e) {
      const p = e.detail || {};
      const route = String(p?.route || '');
      if (route === 'portal' || route === 'inmatch') {
        navigate(`/reveal/${eventId}/${route}`, {
          replace: true,
          state: { stage: p.stageIndex ?? null, fromSocket: true },
        });
      }
    }
    window.addEventListener('reveal:route-intent', onRouteIntent);
    return () => window.removeEventListener('reveal:route-intent', onRouteIntent);
  }, [eventId, navigate]);

  // ── Light tick: if server ends R2 or moves away, leave Verify
  useEffect(() => {
    let stop = false;
    const tick = async () => {
      if (stop) return;
      try {
        const res = await axios.get(
          `${api}/reveal-mode/${eventId}/state?t=${Date.now()}`,
          { ...authHeaders(), validateStatus: (s) => [200,204,410].includes(s) }
        );
        if (res.status === 410) {
          navigate(`/reveal/${eventId}/portal`, { replace: true, state: { revealExpired: true } });
          return;
        }
        if (res.status === 200) {
          const route = String(res?.data?.route || '');
          if (route === 'portal' || route === 'inmatch') {
            navigate(`/reveal/${eventId}/${route}`, {
              replace: true,
              state: {
                stage: Number.isInteger(res?.data?.stageIndex) ? res.data.stageIndex : null,
                matchId: res?.data?.matchId || null,
                stageEndsAt: Number.isFinite(res?.data?.endMs) ? res.data.endMs : null,
              },
            });
            return;
          }
        }
      } catch {
        /* ignore transient */
      }
      setTimeout(tick, 1200);
    };
    tick();
    return () => { stop = true; };
  }, [api, eventId, navigate]);

  
  useEffect(() => {
    if (userId) {
      try {
        socket.emit("joinUserRoom", String(userId));
        L("joinUserRoom", userId);
      } catch (e) {
        L("joinUserRoom error", e?.message);
      }
    }
  }, [socket, userId]);

  /* ---------------- header timer ---------------- */
  function stopTimer() {
    if (tickIdRef.current) {
      clearInterval(tickIdRef.current);
      tickIdRef.current = null;
    }
  }
  function setEndTarget(endAtLike) {
    if (!endAtLike) return;
    const ms = typeof endAtLike === "number" ? endAtLike : new Date(endAtLike).getTime();
    if (!Number.isFinite(ms)) return;
    if (endTargetRef.current && Math.abs(endTargetRef.current - ms) < 1000) return;
    endTargetRef.current = ms;
    const left0 = Math.max(0, ms - Date.now());
    const sec0 = Math.floor(left0 / 1000);
    const mm0 = String(Math.floor(sec0 / 60)).padStart(2, "0");
    const ss0 = String(sec0 % 60).padStart(2, "0");
    lastShownRef.current = `${mm0}:${ss0}`;
    setTparts({ h: "--", m: mm0, s: ss0 });
    stopTimer();
    tickIdRef.current = setInterval(() => {
      const left = Math.max(0, ms - Date.now());
      const sec = Math.floor(left / 1000);
      const mm = String(Math.floor(sec / 60)).padStart(2, "0");
      const ss = String(sec % 60).padStart(2, "0");
      const key = `${mm}:${ss}`;
      if (lastShownRef.current !== key) {
        lastShownRef.current = key;
        setTparts({ h: "--", m: mm, s: ss });
      }
      if (sec <= 0) stopTimer();
    }, 250);
    L("setEndTarget", { endMs: ms, in: Math.round((ms - Date.now()) / 1000) + "s" });
  }
  function readPreloadRoundEnd() {
    const preload = readPreload();
    return preload?.roundEndAt || null;
  }

  /* ---------------- hydrate ---------------- */
  useEffect(() => {
    mountedRef.current = true;
    const preload = readPreload();
    let initialMatchId = location.state?.matchId || preload?.matchId || null;
    let initialPartner = location.state?.partner || preload?.partner || null;
    if (initialPartner) setPartnerSnap(initialPartner);

    const stateEnd = location.state?.roundEndAt || null;
    const preloadEnd = readPreloadRoundEnd();
    if (stateEnd) setEndTarget(stateEnd);
    else if (preloadEnd) setEndTarget(preloadEnd);

    async function ensureMatchId() {
      if (initialMatchId) {
        setMatchId(initialMatchId);
        L("ensureMatchId: from nav/preload", initialMatchId);
        return initialMatchId;
      }
      try {
        const { data, status } = await axios.get(
          `${api}/reveal-mode/${eventId}/match?t=${Date.now()}`,
          authHeaders()
        );
        L("GET /reveal-mode/:id/match (ensureMatchId)", status, data?.state, { cnt: data?.matches?.length });
        if (data?.state === "match" && Array.isArray(data.matches) && data.matches.length) {
          const first = data.matches[0];
          const id = first.matchId || null;
          setMatchId(id);
          if (first.partner && !partnerSnap) setPartnerSnap(first.partner);
          setEndTarget(first.roundEndAt || data.roundEndAt);
          return id;
        }
      } catch (e) {
        L("ensureMatchId error", e?.response?.status, e?.message);
      }
      return null;
    }

    (async () => {
      const id = await ensureMatchId();
      if (id) {
        try {
          socket.emit("initVerification", { matchId: id, userId });
          L("emit initVerification", { matchId: id, userId });
        } catch (e) {
          L("emit initVerification error", e?.message);
        }
        try {
          const { data, status } = await axios.get(`${api}/matches/${id}`, authHeaders());
          L("GET /matches/:id", status, data?.match?.status);
          const doc = data?.match || data || null;
          if (doc && mountedRef.current) {
            setMatch(doc);
            const fp = composePartnerFromMatch(doc, userId);
            if (fp && !partnerSnap) setPartnerSnap(fp);
            setEndTarget(doc?.endTime || doc?.expiresAt || null);
          }
        } catch (e) {
          L("GET /matches/:id error", e?.response?.status, e?.message);
        }
      }
      setBooting(false);
    })();

    return () => {
      mountedRef.current = false;
      stopTimer();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api, eventId, socket, userId]);

  /* ---------------- socket listeners ---------------- */
  useEffect(() => {
    const onUpdated = (updated) => {
      if (!mountedRef.current || !updated) return;
      const mine = updated?.participants?.some((p) => String(p?._id || p) === String(userId)) || false;
      if (!mine) return;
      L("socket matchUpdated", {
        status: updated?.status,
        codeConfirmed: !!updated?.codeConfirmed,
        endTime: updated?.endTime,
        expiresAt: updated?.expiresAt
      });

      const freshPartner = composePartnerFromMatch(updated, userId);
      if (freshPartner) setPartnerSnap(freshPartner);
      setMatch(updated);
      setEndTarget(updated?.endTime || updated?.expiresAt || null);

      if (updated?.codeConfirmed) {
        if (!verificationSuccess) {
          setVerificationSuccess(true);
          setCountdownValue(3);
          L("code confirmed → start countdown", { countdown: 3 });
        }
      }
    };

    const onReconnect = () => {
      L("socket reconnect → refreshHard");
      refreshHard();
    };

    socket.on("matchUpdated", onUpdated);
    socket.on("connect", onReconnect);
    return () => {
      socket.off("matchUpdated", onUpdated);
      socket.off("connect", onReconnect);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [socket, userId, verificationSuccess]);

  /* ---------------- revalidation on focus/visible ---------------- */
  useEffect(() => {
    const recheck = () => {
      L("visibility/focus → refreshHard");
      refreshHard();
    };
    const onVisible = () => document.visibilityState === "visible" && recheck();
    const onFocus = () => recheck();
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", onFocus);
    return () => {
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", onFocus);
    };
    // eslint-disable-next-line
  }, [matchId]);

  async function refreshHard() {
    if (refreshingRef.current) return;
    refreshingRef.current = true;
    L("refreshHard: begin", { matchId });
    try {
      if (matchId) {
        try {
          const { data, status } = await axios.get(`${api}/matches/${matchId}`, authHeaders());
          L("refreshHard GET /matches/:id", status, data?.match?.status);
          const doc = data?.match || data || null;
          if (doc && mountedRef.current) {
            setMatch(doc);
            const fp = composePartnerFromMatch(doc, userId);
            if (fp) setPartnerSnap(fp);
            setEndTarget(doc?.endTime || doc?.expiresAt || null);
          }
        } catch (e) {
          L("refreshHard error /matches/:id", e?.response?.status, e?.message);
        }
        try { socket.emit("initVerification", { matchId, userId }); L("emit initVerification (refresh)"); } catch {}
        return;
      }
      try {
        const { data, status } = await axios.get(
          `${api}/reveal-mode/${eventId}/match?t=${Date.now()}`,
          authHeaders()
        );
        L("refreshHard GET /reveal-mode/:id/match", status, data?.state, { cnt: data?.matches?.length });
        if (data?.state === "match" && Array.isArray(data.matches) && data.matches.length) {
          const first = data.matches[0];
          const id = first.matchId || null;
          setMatchId(id);
          if (first.partner) setPartnerSnap(first.partner);
          setEndTarget(first.roundEndAt || data.roundEndAt);
          if (id) {
            try { socket.emit("initVerification", { matchId: id, userId }); L("emit initVerification (new id)", id); } catch {}
          }
        }
      } catch (e) {
        L("refreshHard error /reveal-mode/:id/match", e?.response?.status, e?.message);
      }
    } finally {
      refreshingRef.current = false;
      L("refreshHard: end");
    }
  }

  /* ---------------- tick down the 3..2..1 ---------------- */
  useEffect(() => {
    if (!verificationSuccess) return;
    if (countdownValue <= 0) return;
  
    L("countdown tick", countdownValue);
    const id = setTimeout(() => setCountdownValue((v) => v - 1), 1000);
  
    return () => clearTimeout(id);
  }, [verificationSuccess, countdownValue]);
  
  /* ---------------- handoff → start session (5:00) → navigate ---------------- */
  const handoffRanRef = useRef(false); // guard against double-run (StrictMode/reconnect)
  useEffect(() => {
    if (!verificationSuccess) return;
    if (handoffRanRef.current) return; // already navigated once
  
    async function go() {

      // 1) Flip to in-session (authoritative 5:00)
      try {
        if (matchId) {
          const { data } = await axios.post(`${api}/matches/${matchId}/start-session`, {}, authHeaders());
          const m = data?.match || null;
          if (m?.endTime) {
            const ms = new Date(m.endTime).getTime();
            if (Number.isFinite(ms)) endTargetRef.current = ms;
          }
        } else {
          L("handoff: no matchId to start-session (will still seed 5:00)");
        }
      } catch (e) {
        L("POST /matches/:id/start-session error (continuing anyway)", e?.response?.status, e?.message);
      }

      // Count matches (to know where to send user after session)
      let count = readPreloadMatchesCount();
      if (count == null) count = await fetchMatchesCount(api, eventId);
      const hasTwo = (count || 0) > 1;
      L("handoff: matches count", { count, hasTwo });
  
      let question = match?.icebreakerQuestion || "";
      let stageEndsAt =
        (match?.status === "in-session" && match?.endTime)
          ? new Date(match.endTime).getTime()
          : Date.now() + 5 * 60 * 1000;
  
      L("handoff: inmatch endsAt", {
        stageEndsAt,
        in: Math.round((stageEndsAt - Date.now()) / 1000) + "s",
      });
  
      const safeEndsAt =
        Number.isFinite(stageEndsAt) && stageEndsAt > Date.now() + 2000
          ? stageEndsAt
          : Date.now() + 5 * 60 * 1000;
  
      L("NAV → /inmatch", {
        eventId,
        hasTwo,
        stageEndsAt: safeEndsAt,
        in: Math.round((safeEndsAt - Date.now()) / 1000) + "s",
        icebreakerQuestion: question,
      });
  
      handoffRanRef.current = true; // prevent double navigation
      navigate(`/reveal/${eventId}/inmatch`, {
        replace: true,
        state: { stageEndsAt: safeEndsAt, icebreakerQuestion: question, hasTwo, __fromVerify: true, matchId },
      });
    }
  
    if (countdownValue <= 0) {
      go();
    } else {
      const t = setTimeout(go, countdownValue * 1000);
      return () => clearTimeout(t);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [verificationSuccess, countdownValue]);
  
  

  /* ---------------- input handlers (unchanged) ---------------- */
  function handle2pChange(idx, e) {
    const sanitize = (c) => (c || "").toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 1);
    const val = sanitize(e.target.value);
    setCodeDigits((prev) => {
      const next = [...prev];
      next[idx] = val;
      if (val && idx < 2) codeDigitsRefs.current[idx + 1]?.current?.focus();
      return next;
    });
  }
  function handle2pKeyDown(idx, e) {
    if (e.key === "Backspace") {
      e.preventDefault();
      setCodeDigits((prev) => {
        const next = [...prev];
        if (next[idx]) {
          next[idx] = "";
          return next;
        }
        if (idx > 0) {
          codeDigitsRefs.current[idx - 1]?.current?.focus();
          next[idx - 1] = "";
        }
        return next;
      });
    }
  }
  function submit2p() {
    const attempt = codeDigits.join("");
    if (!attempt || attempt.length < 3 || !matchId) return;
    try {
      L("emit verifyCode", { matchId, userId, attempt });
      socket.emit("verifyCode", {
        matchId,
        userId,
        codeAttempt: attempt,
        sessionId: localStorage.getItem("sessionId") || "",
      });
    } catch (e) {
      L("emit verifyCode error", e?.message);
    }
  }

  /* ---------------- render (unchanged) ---------------- */
  const iAmHolder = String(match?.codeHolderId || "") === String(userId);
  const partner =
    (match?.participants || []).find((p) => String(p?._id) !== String(userId)) ||
    partnerSnap ||
    null;

  const partnerName = partner
    ? `${partner.firstName || "Partner"}${
        partner.lastName ? " " + partner.lastName.charAt(0).toUpperCase() + "." : ""
      }`.trim()
    : "Your partner";

  const canSubmit = codeDigits.every((d) => d.length === 1);

  if (!matchId && booting) {
    return (
      <div style={ST.fallbackContainer}>
        <h2 style={ST.fallbackTitle}>Loading…</h2>
        <p style={ST.fallbackText}>Preparing your reveal verification.</p>
      </div>
    );
  }

  return (
    <div key={location.key} style={ST.page}>
      <style>{`@keyframes codeShake{0%,100%{transform:translateX(0)}10%,90%{transform:translateX(-4px)}20%,80%{transform:translateX(4px)}30%,50%,70%{transform:translateX(-8px)}40%,60%{transform:translateX(8px)}}`}</style>

      <RevealPortalNavBar eventId={eventId} title="Code Verification" />

      {/* header & timer ... (unchanged UI below) */}
      <div style={ST.header.container}>
        <h1 style={ST.header.title}>
          Enter your{" "}
          <span style={ST.header.highlight}>
            <span style={ST.header.highlightText}>Code</span>
          </span>
        </h1>

        <div style={ST.header.subheading}>
          Share or input the 3-character code to confirm your match.
        </div>

        <div style={ST.timerWrapper}>
          <div style={ST.timerLabelPill}>Timer Ends In</div>
          <div style={ST.digitsRow} aria-live="polite">
            {hasHours && (
              <>
                <span style={ST.digit}>{tparts.h[0]}</span>
                <span style={ST.digit}>{tparts.h[1]}</span>
                <span style={ST.colon}>:</span>
              </>
            )}
            <span style={ST.digit}>{tparts.m[0]}</span>
            <span style={ST.digit}>{tparts.m[1]}</span>
            <span style={ST.colon}>:</span>
            <span style={ST.digit}>{tparts.s[0]}</span>
            <span style={ST.digit}>{tparts.s[1]}</span>
          </div>
        </div>
      </div>

      {/* Divider under timer */}
      {(() => {
        let label = "Enter partner code";
        if (iAmHolder) {
          label = match?.codeConfirmed ? "Code confirmed" : "Share this code";
        } else {
          label = match?.codeValue ? "Enter partner code" : "Waiting for partner";
        }
        return (
          <div style={ST.dividerRow} aria-live="polite">
            <div style={ST.dividerLine} />
            <div style={ST.dividerText}>{label}</div>
            <div style={ST.dividerLine} />
          </div>
        );
      })()}

      {/* Code area */}
      <div style={ST.code.container}>
        {match?.codeConfirmed && verificationSuccess ? (
          <div style={{ textAlign: "center" }}>
            <p style={ST.successText}>Verification successful!</p>
            <p style={ST.countdownText}>Continuing in {String(countdownValue)}…</p>
          </div>
        ) : iAmHolder ? (
          <>
            <div
              style={{ ...ST.boxRow, animation: shouldShake ? "codeShake 0.6s" : "none", marginTop: 12 }}
              onAnimationEnd={(e) => {
                if (e.animationName === "codeShake") setShouldShake(false);
              }}
            >
              {(match?.codeValue || "---")
                .toUpperCase()
                .split("")
                .map((digit, idx) => (
                  <div key={idx} style={ST.readOnlyBox}>
                    {digit || "0"}
                  </div>
                ))}
            </div>
            <div style={ST.helper}>
              Share this code with{" "}
              <span style={ST.namePill}>
                <img
                  src={partner?.profilePicUrl || "/default-avatar.png"}
                  alt=""
                  style={ST.nameAvatar}
                />
                {partnerName}
              </span>
              .
            </div>
          </>
        ) : (
          <>
            <div
              style={{ ...ST.boxRow, animation: shouldShake ? "codeShake 0.6s" : "none" }}
              onAnimationEnd={(e) => {
                if (e.animationName === "codeShake") {
                  setCodeDigits(["", "", ""]);
                  codeDigitsRefs.current[0]?.current?.focus();
                  setShouldShake(false);
                }
              }}
            >
              {codeDigits.map((digit, idx) => (
                <input
                  key={idx}
                  type="text"
                  inputMode="text"
                  autoComplete="off"
                  maxLength={1}
                  placeholder="0"
                  ref={codeDigitsRefs.current[idx]}
                  value={(digit || "").toUpperCase()}
                  onChange={(e) => handle2pChange(idx, e)}
                  onKeyDown={(e) => handle2pKeyDown(idx, e)}
                  style={ST.inputBox}
                />
              ))}
            </div>

            <div style={ST.helper}>
              Input{" "}
              <span style={ST.namePill}>
                <img
                  src={partner?.profilePicUrl || "/default-avatar.png"}
                  alt=""
                  style={ST.nameAvatar}
                />
                {partnerName}’s
              </span>{" "}
              code here.
            </div>
          </>
        )}
      </div>

      {/* Action bar — fixed to bottom */}
      {!match?.codeConfirmed && !verificationSuccess && (
        <div style={ST.actionBar}>
          <button onClick={() => { L("Back clicked"); navigate(-1); }} style={{ ...ST.actionSecondary, flex: 1 }}>
            Back
          </button>
          {!iAmHolder && (
            <button
              onClick={submit2p}
              disabled={!canSubmit}
              style={{ ...ST.actionButton, flex: 2, ...(canSubmit ? {} : ST.actionButtonDisabled) }}
            >
              Enter
            </button>
          )}
        </div>
      )}
    </div>
  );
}


/* =================== STYLES (mirrors MatchFlow CodeVerificationPage for 1–1) =================== */
const ST = {
  page: {
    minHeight: "100vh",
    background: "#fff",
    fontFamily:
      "'Inter', system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif",
    display: "flex",
    flexDirection: "column",
  },

  /* Mixer-style header */
  header: {
    container: {
      boxSizing: "border-box",
      maxWidth: 680,
      width: "80%",
      margin: "12px auto 8px",
      padding: "0 12px",
      textAlign: "center",
      color: "#0b0b0b",
    },
    title: {
      margin: 0,
      fontSize: "1.85rem",
      fontWeight: 800,
      lineHeight: 1.2,
      letterSpacing: "-0.01em",
    },
    highlight: {
      display: "inline-block",
      backgroundColor: "#3579F6",
      color: "#fff",
      padding: "3px 10px",
      paddingBottom: 4,
      borderRadius: 6,
      transform: "skewX(-6deg) rotate(-2deg)",
      marginLeft: 0,
    },
    highlightText: {
      display: "inline-block",
      transform: "skewX(6deg) rotate(2deg)",
    },
    subheading: {
      marginTop: 6,
      fontSize: ".9rem",
      color: "#6b7280",
      fontWeight: 500,
      lineHeight: 1.45,
    },
  },

  /* Code section */
  code: {
    container: {
      boxSizing: "border-box",
      width: "100%",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      padding: "0 12px 96px",
      marginTop: 20,
    },
  },

  /* Divider under timer */
  dividerRow: {
    display: "flex",
    alignItems: "center",
    fontSize: ".72rem",
    color: "#6b7280",
    fontWeight: 800,
    letterSpacing: ".08em",
    textTransform: "uppercase",
    margin: "24px 0 -12px",
    width: "100%",
    maxWidth: 400,
    marginLeft: "auto",
    marginRight: "auto",
  },
  dividerLine: { flex: 1, height: 1, backgroundColor: "#e5e7eb" },
  dividerText: { margin: "12px 0px" },

  /* Input rows */
  boxRow: {
    display: "flex",
    justifyContent: "center",
    gap: 10,
    margin: "12px 0 8px",
    width: "100%",
    maxWidth: 400,
  },
  inputBox: {
    width: 62,
    height: 70,
    borderRadius: 12,
    background: "#fff",
    border: "1px solid #e5e7eb",
    textAlign: "center",
    fontSize: "1.5rem",
    fontWeight: 900,
    color: "#0f172a",
    textTransform: "uppercase",
    fontVariantNumeric: "tabular-nums",
    outline: "none",
    transition: "box-shadow .14s ease, border-color .14s ease",
    boxShadow: "0 1px 2px rgba(0,0,0,0.04)",
  },
  readOnlyBox: {
    width: 62,
    height: 70,
    borderRadius: 12,
    background: "#f8fafc",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "1.5rem",
    fontWeight: 900,
    color: "#9ca3af",
    border: "1px solid #f8fafc",
    textTransform: "uppercase",
    fontVariantNumeric: "tabular-nums",
  },

  helper: {
    fontSize: ".85rem",
    lineHeight: 1.5,
    color: "#9ca3af",
    maxWidth: 300,
    marginLeft: "auto",
    marginRight: "auto",
    marginTop: 10,
    marginBottom: 8,
    textAlign: "center",
  },

  /* Action bar */
  actionBar: {
    position: "fixed",
    inset: "auto 0 0 0",
    background: "#fff",
    borderTop: "1px solid #eef0f2",
    display: "flex",
    gap: 10,
    padding: "12px 16px calc(12px + env(safe-area-inset-bottom, 0px))",
    boxSizing: "border-box",
    maxWidth: 760,
    margin: "0 auto",
    left: "50%",
    transform: "translateX(-50%)",
    width: "100%",
    zIndex: 50,
  },
  actionButton: {
    flex: 1,
    height: 44,
    borderRadius: 12,
    border: "1px solid #0b0b0b",
    background: "#0b0b0b",
    color: "#fff",
    fontSize: 12,
    fontWeight: 800,
    letterSpacing: 0.2,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
  },
  actionSecondary: {
    height: 44,
    borderRadius: 12,
    border: "1px solid #d1d5db",
    background: "#fff",
    color: "#0b0b0b",
    fontSize: 12,
    fontWeight: 800,
    letterSpacing: 0.2,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
  },
  actionButtonDisabled: {
    borderColor: "#d1d5db",
    background: "#d1d5db",
    color: "#fff",
    cursor: "not-allowed",
  },

  /* Fallback */
  fallbackContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    minHeight: "100vh",
    background: "#fff",
    padding: "1rem",
    textAlign: "center",
    fontFamily:
      "'Inter', system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif",
  },
  fallbackTitle: {
    fontSize: "1.2rem",
    fontWeight: 700,
    margin: "1rem 0 0.5rem",
    color: "#101828",
  },
  fallbackText: {
    fontSize: "0.95rem",
    color: "#475569",
    marginBottom: "1.5rem",
    maxWidth: 400,
  },

  /* Header timer */
  timerWrapper: {
    marginTop: 18,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
  },
  timerLabelPill: {
    display: "inline-block",
    padding: "4px 10px",
    borderRadius: 999,
    background: "#fff",
    border: "1px solid #e5e7eb",
    fontSize: ".7rem",
    color: "#374151",
    fontWeight: 800,
    letterSpacing: ".06em",
    textTransform: "uppercase",
    marginBottom: 8,
  },
  digitsRow: { display: "flex", alignItems: "baseline", gap: 4, marginBottom: -6 },
  digit: {
    display: "inline-block",
    fontSize: "2.5rem",
    lineHeight: 1,
    fontWeight: 900,
    letterSpacing: "-0.02em",
    color: "#0f172a",
    fontVariantNumeric: "tabular-nums",
    marginTop: 8,
  },
  colon: {
    fontSize: "2rem",
    fontWeight: 900,
    color: "#9ca3af",
    lineHeight: 1,
    padding: "8px 4px",
    transform: "translateY(-5px)",
  },

  /* Name pill */
  namePill: {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    padding: "3px 8px 3px 2px",
    borderRadius: 999,
    border: "1px solid #e5e7eb",
    background: "#fff",
    color: "#0f172a",
    fontSize: ".85rem",
    fontWeight: 800,
    lineHeight: 1,
    whiteSpace: "nowrap",
    boxShadow: "0 1px 2px rgba(15,23,42,0.04)",
    transform: "translateY(4px)",
  },
  nameAvatar: {
    width: 18,
    height: 18,
    borderRadius: "50%",
    objectFit: "cover",
    display: "block",
    border: "1px solid #fff",
  },

  /* Pager (unused here but kept for parity) */
  pagerRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
    margin: "6px 0 6px",
    marginTop: 10,
  },
  pagerBtn: {
    height: 32,
    width: 40,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: 10,
    border: "1px solid #e5e7eb",
    background: "#fff",
    color: "#0f172a",
    boxShadow: "0 1px 2px rgba(15,23,42,0.04)",
    cursor: "pointer",
    transition: "transform .12s ease, box-shadow .12s ease, opacity .12s ease",
    outline: "none",
  },
  pagerBtnDisabled: { opacity: 0.45, cursor: "not-allowed" },
  pagerIcon: { fontSize: 16 },
  pagerText: {
    minWidth: 48,
    textAlign: "center",
    fontSize: 12,
    fontWeight: 500,
    letterSpacing: ".5em",
    color: "#0f172a",
  },

  /* Success text */
  successText: {
    fontSize: "1rem",
    color: "#059669",
    fontWeight: 700,
    margin: 0,
  },
  countdownText: {
    fontSize: "0.9rem",
    color: "#6B7280",
    margin: "0.5rem 0 0",
  },
};
